import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import qrcode from 'qrcode-generator';
import { t, getLocale } from '../core/i18n.js';
import { renderMultiline } from '../core/format.js';

export class PropertySheetView extends Component {
  constructor(ctx) {
    super();
    this.id = (ctx && ctx.params && ctx.params.id) || '';
    this.state = { loading: true, property: null, error: null };
  }

  template() {
    if (this.state.loading) {
      return `<section class="stack"><div class="muted">${esc(t('sheet.loading'))}</div></section>`;
    }
    if (this.state.error) {
      return `
        <section class="stack">
          <div class="card stack">
            <h2>${esc(t('sheet.unavailable'))}</h2>
            <div class="error-banner">${esc(this.state.error)}</div>
          </div>
        </section>
      `;
    }
    const p = this.state.property;
    const slug = p.public_slug || '';
    const url = slug ? `${window.location.origin}/#/p/${slug}` : '';
    const urlDisplay = url.replace(/^https?:\/\//, '').replace(/\/#\//, '/');
    const sub = [p.postal_code, p.city]
      .filter((s) => s !== null && s !== undefined && String(s).trim() !== '')
      .map((s) => esc(s))
      .join(' ');
    const descHtml = p.public_description ? renderMultiline(p.public_description) : '';
    const headline = (p.public_title && String(p.public_title).trim()) || p.name;
    return `
      <section class="stack sheet">
        <div class="row-between no-print">
          <a class="btn btn-ghost" href="#/properties/${esc(this.id)}">${esc(t('sheet.back'))}</a>
          <button class="btn btn-primary" data-action="print">${esc(t('sheet.print'))}</button>
        </div>
        ${!Number(p.is_published) ? `
          <div class="warn-banner no-print">
            ${esc(t('sheet.not_published_warning'))}
          </div>` : ''}
        <article class="sheet-page">
          <header class="sheet-head">
            <span class="sheet-brand">${esc(t('sheet.flyer.brand'))}</span>
            <span class="sheet-meta">${esc(t('sheet.flyer.meta'))}</span>
          </header>

          <div class="stack" style="gap:6mm">
            <section class="sheet-hero">
              <div>
                <div class="sheet-eyebrow">${esc(t('sheet.flyer.eyebrow_for_rent'))}</div>
                <h1 class="sheet-headline">${esc(headline)}</h1>
                <div class="sheet-sub">${sub || esc(t('sheet.flyer.sub_no_address'))}</div>
              </div>
            </section>

            ${descHtml ? `<div class="sheet-desc">${descHtml}</div>` : ''}

            <section class="sheet-qr-row">
              <div data-qr></div>
              <div class="sheet-qr-copy">
                <div class="eyebrow">${esc(t('sheet.flyer.qr_eyebrow'))}</div>
                <div class="head">${esc(t('sheet.flyer.qr_headline'))}</div>
                ${urlDisplay ? `<div class="url">${esc(urlDisplay)}</div>` : ''}
                ${(p.public_contact_email || p.public_contact_phone) ? `
                  <div class="contact">${esc(t('sheet.flyer.qr_contact_prefix'))}${
                    [p.public_contact_email, p.public_contact_phone]
                      .filter(Boolean).map((s) => esc(s)).join(' · ')
                  }</div>` : ''}
              </div>
            </section>

            <section class="sheet-b2b">
              <div>
                <div class="eyebrow">${esc(t('sheet.flyer.b2b_eyebrow'))}</div>
                <div class="head">${esc(t('sheet.flyer.b2b_head'))}</div>
                <div class="body">${esc(t('sheet.flyer.b2b_body'))}</div>
              </div>
              <div class="cta">${esc(t('sheet.flyer.b2b_cta'))}</div>
            </section>
          </div>
        </article>
      </section>
    `;
  }

  mount() {
    const printBtn = this.el && this.el.querySelector('[data-action="print"]');
    if (printBtn) printBtn.addEventListener('click', () => this._downloadPdf(printBtn));
    if (this.state.property === null && !this.state.error) {
      this._load();
    } else if (this.state.property) {
      this._renderQr();
    }
  }

  async _downloadPdf(btn) {
    if (btn.disabled) return;
    btn.disabled = true;
    try {
      const blob = await api.get(
        `/properties/${encodeURIComponent(this.id)}/sheet.pdf?lang=${encodeURIComponent(getLocale())}`,
        { responseType: 'blob' }
      );
      const p = this.state.property || {};
      const baseName = (p.public_title && String(p.public_title).trim()) || p.name || 'property';
      const safeName = String(baseName)
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'property';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('sheet.errors.load');
      this.setState({ error: msg });
    } finally {
      btn.disabled = false;
    }
  }

  _renderQr() {
    const slot = this.el && this.el.querySelector('[data-qr]');
    const p = this.state.property;
    if (!slot || !p || !p.public_slug) return;
    const url = `${window.location.origin}/#/p/${p.public_slug}`;
    try {
      const qr = qrcode(0, 'M');
      qr.addData(url);
      qr.make();
      slot.innerHTML = qr.createSvgTag({ cellSize: 6, margin: 2, scalable: true });
      const svg = slot.querySelector('svg');
      if (svg) {
        svg.removeAttribute('width');
        svg.removeAttribute('height');
        svg.setAttribute('style', 'width:38mm; height:38mm');
      }
    } catch (_) { /* noop */ }
  }

  async _load() {
    try {
      const property = await api.get(`/properties/${this.id}`);
      this.setState({ property, loading: false });
    } catch (err) {
      this.setState({
        error: err instanceof ApiError ? err.message : t('sheet.errors.load'),
        loading: false,
      });
    }
  }
}
